import React from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  description?: string;
  icon: React.ReactElement<{ className?: string }>;
  iconGradient?: "blue" | "purple" | "green" | "pink" | "orange" | "violet" | "emerald" | "rose";
  actions?: React.ReactNode;
  className?: string;
  eyebrow?: string;
}

const toneMap = {
  blue: "bg-sky-50 text-sky-700 ring-sky-100",
  purple: "bg-violet-50 text-violet-700 ring-violet-100",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  pink: "bg-pink-50 text-pink-700 ring-pink-100",
  orange: "bg-amber-50 text-amber-700 ring-amber-100",
  violet: "bg-violet-50 text-violet-700 ring-violet-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  rose: "bg-rose-50 text-rose-700 ring-rose-100",
};

export function PageHeader({
  title,
  description,
  icon,
  iconGradient = "blue",
  actions,
  className,
  eyebrow = "Διαχείριση",
}: PageHeaderProps) {
  return (
    <section
      className={cn(
        "flex flex-col gap-5 border-b border-slate-200/80 pb-5 sm:flex-row sm:items-end sm:justify-between sm:pb-6",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3.5 sm:items-center">
        <div
          className={cn(
            "mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1 sm:mt-0 sm:h-12 sm:w-12",
            toneMap[iconGradient],
          )}
        >
          {React.cloneElement(icon, { className: "h-5 w-5 sm:h-6 sm:w-6" })}
        </div>
        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {eyebrow}
          </p>
          <h1 className="text-2xl font-bold tracking-[-0.025em] text-slate-950 sm:text-[1.8rem] sm:leading-9">
            {title}
          </h1>
          {description && (
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500 sm:text-[15px]">
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
          {actions}
        </div>
      )}
    </section>
  );
}
